import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { describeApiError, getProduct } from '../services/api';
import { useCart } from '../context/CartContext';
import PersonalizationFields from '../components/PersonalizationFields';
import type { Product, CartLinePersonalization } from '../types';
import { formatCents } from '../utils/money';
import { useHasProducts } from '../hooks/useHasProducts';

const ProductDetails: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const hasProducts = useHasProducts();
  const { addLine } = useCart();

  const [product, setProduct] = useState<Product | null>(null);
  const [size, setSize] = useState('');
  const [color, setColor] = useState('');
  const [threadColor, setThreadColor] = useState('');
  const [font, setFont] = useState('');
  const [personalization, setPersonalization] = useState<CartLinePersonalization>({});
  const [qty, setQty] = useState(1);
  const [itemNotes, setItemNotes] = useState('');
  const [imgIdx, setImgIdx] = useState(0);
  const [error, setError] = useState<string | undefined>();
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (!slug) return;
    getProduct(slug)
      .then(p => {
        setProduct(p);
        const sizes = p.size_options ?? [];
        const colors = p.color_options ?? [];
        const threads = p.thread_color_options ?? [];
        const fonts = p.font_options ?? [];
        if (sizes.length === 1) setSize(sizes[0]);
        if (colors.length === 1) setColor(colors[0]);
        if (threads.length === 1) setThreadColor(threads[0]);
        if (fonts.length === 1) setFont(fonts[0]);
      })
      .catch(e => {
        // A 404 covers both "never existed" and "unpublished" — the API
        // deliberately doesn't distinguish them for the public.
        setUnavailable(e?.response?.status === 404);
        setError(describeApiError(e, 'That product could not be loaded.'));
      });
  }, [slug]);

  // Compute the carousel slice based on the selected color. If no color is
  // selected (or no images match), fall back to the full image set.
  const visibleImages = useMemo(() => {
    if (!product) return [] as string[];
    const all = product.images ?? [];
    if (!color) return all;
    const map = product.image_color_map ?? {};
    const matched = all.filter(url => map[url] === color);
    return matched.length > 0 ? matched : all;
  }, [product, color]);

  // Reset to first image when the visible set changes.
  useEffect(() => {
    setImgIdx(0);
  }, [color, product?.product_id]);

  // A withdrawn or missing product is a normal thing for a customer to
  // land on — an old link, a shared screenshot — so it gets a way forward
  // rather than an error in red.
  if (unavailable) {
    return (
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-16 text-center">
        <h1 className="font-serif text-2xl mb-3">This piece isn't available right now</h1>
        <p className="text-text-light mb-6">
          It may have been a one-off, or it's taking a break from the shop.
          Everything here is made to order, so if it's what you're after we
          can almost certainly make it for you.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link
            to="/custom-request"
            className="bg-primary text-white rounded px-5 py-3 text-sm font-medium hover:bg-primary-dark"
          >
            Request it as a custom piece
          </Link>
          <Link
            to={hasProducts ? '/shop' : '/home'}
            className="border border-primary text-primary rounded px-5 py-3 text-sm font-medium hover:bg-primary/5"
          >
            {hasProducts ? 'Browse the shop' : 'Back to home'}
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 text-red-600">{error}</div>;
  }
  if (!product) {
    return <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 text-text-light">Loading…</div>;
  }

  const sizes = product.size_options ?? [];
  const colors = product.color_options ?? [];
  const threads = product.thread_color_options ?? [];
  const fonts = product.font_options ?? [];
  const inStock = product.in_stock !== false;

  // Effective price: size override beats base.
  const effectivePriceCents = (() => {
    const override = size ? product.size_price_overrides?.[size] : undefined;
    if (typeof override === 'number') return override;
    return product.price_cents ?? 0;
  })();

  const requirementsMet =
    (!sizes.length || !!size) &&
    (!colors.length || !!color) &&
    (!threads.length || !!threadColor) &&
    (!fonts.length || !!font);
  const canAdd = inStock && qty > 0 && requirementsMet;

  const cover = visibleImages[imgIdx];
  const showCarouselControls = visibleImages.length > 1;
  const nextImage = () => setImgIdx(i => (i + 1) % visibleImages.length);
  const prevImage = () =>
    setImgIdx(i => (i - 1 + visibleImages.length) % visibleImages.length);

  const handleAdd = () => {
    for (const f of product.personalization_fields ?? []) {
      if (f.required && !personalization[f.key]) {
        setError(`Please fill in: ${f.label}`);
        return;
      }
    }

    const fullPersonalization: CartLinePersonalization = { ...personalization };
    if (size) fullPersonalization.size = size;
    if (color) fullPersonalization.color = color;
    if (threadColor) fullPersonalization.thread_color = threadColor;
    if (font) fullPersonalization.font = font;

    const parts: string[] = [];
    if (size) parts.push(size);
    if (color) parts.push(color);
    if (threadColor) parts.push(`thread: ${threadColor}`);
    if (font) parts.push(`font: ${font}`);
    const variantLabel = parts.join(' / ') || 'Standard';

    addLine({
      product_id: product.product_id,
      product_name: product.name,
      product_image: cover,
      sku: product.product_id,
      variant_label: variantLabel,
      unit_price_cents: effectivePriceCents,
      qty,
      personalization: Object.keys(fullPersonalization).length ? fullPersonalization : undefined,
      notes: itemNotes.trim() || undefined,
    });
    navigate('/cart');
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid md:grid-cols-2 gap-10">
      <div className="space-y-3">
        <div className="relative aspect-square bg-background-dark rounded overflow-hidden">
          {cover ? (
            <img src={cover} alt={product.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-text-light">
              No image
            </div>
          )}
          {showCarouselControls && (
            <>
              <button
                type="button"
                onClick={prevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-1.5 shadow hover:bg-white"
                aria-label="Previous image"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                onClick={nextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-1.5 shadow hover:bg-white"
                aria-label="Next image"
              >
                <ChevronRight size={18} />
              </button>
              <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs rounded px-2 py-0.5">
                {imgIdx + 1} / {visibleImages.length}
              </div>
            </>
          )}
        </div>
        {visibleImages.length > 1 && (
          <div className="flex gap-2 overflow-x-auto">
            {visibleImages.map((url, i) => (
              <button
                key={url + i}
                type="button"
                onClick={() => setImgIdx(i)}
                className={`shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition ${
                  i === imgIdx ? 'border-primary' : 'border-transparent opacity-70 hover:opacity-100'
                }`}
              >
                <img src={url} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-5">
        <h1 className="text-3xl font-semibold">{product.name}</h1>
        <div className="text-xl">{formatCents(effectivePriceCents)}</div>
        <p className="text-text-light text-sm whitespace-pre-line">{product.description}</p>

        {!inStock && (
          <div className="text-sm text-red-600 font-medium uppercase tracking-wide">
            Currently out of stock
          </div>
        )}

        {sizes.length > 0 && (
          <div>
            <label className="block text-sm font-medium mb-1">Size</label>
            <select
              value={size}
              onChange={e => setSize(e.target.value)}
              className="w-full border border-background-dark rounded px-3 py-2 text-sm bg-white"
            >
              <option value="">— select size —</option>
              {sizes.map(s => {
                const override = product.size_price_overrides?.[s];
                const priceLabel =
                  typeof override === 'number' && override !== (product.price_cents ?? 0)
                    ? ` (${formatCents(override)})`
                    : '';
                return (
                  <option key={s} value={s}>
                    {s}
                    {priceLabel}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        {colors.length > 0 && (
          <div>
            <label className="block text-sm font-medium mb-1">Color</label>
            <select
              value={color}
              onChange={e => setColor(e.target.value)}
              className="w-full border border-background-dark rounded px-3 py-2 text-sm bg-white"
            >
              <option value="">— select color —</option>
              {colors.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        )}

        {threads.length > 0 && (
          <div>
            <label className="block text-sm font-medium mb-1">Thread color</label>
            <select
              value={threadColor}
              onChange={e => setThreadColor(e.target.value)}
              className="w-full border border-background-dark rounded px-3 py-2 text-sm bg-white"
            >
              <option value="">— select thread color —</option>
              {threads.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        )}

        {fonts.length > 0 && (
          <div>
            <label className="block text-sm font-medium mb-1">Font</label>
            <select
              value={font}
              onChange={e => setFont(e.target.value)}
              className="w-full border border-background-dark rounded px-3 py-2 text-sm bg-white"
            >
              <option value="">— select font —</option>
              {fonts.map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>
        )}

        <PersonalizationFields
          fields={product.personalization_fields ?? []}
          values={personalization}
          onChange={setPersonalization}
        />

        {/* Always offered, whatever options the product defines. The
            dropdowns can only cover what was thought of in advance, and
            custom work is mostly the things that weren't -- a name's
            spelling, a thread to match something they already own. Kept
            per item, since a cart with two pieces needs two answers. */}
        <div>
          <label
            htmlFor="item-notes"
            className="block text-sm font-medium mb-1"
          >
            Personalization details
          </label>
          <p className="text-xs text-text-light mb-2">
            Names and exactly how to spell them, thread colours, monogram
            initials, sizing notes — anything we need to make this one right.
          </p>
          <textarea
            id="item-notes"
            rows={3}
            value={itemNotes}
            onChange={e => setItemNotes(e.target.value)}
            placeholder="e.g. Name: Emmaline (not Emmeline), sage green thread, monogram ELB"
            className="w-full border border-background-dark rounded px-3 py-2 text-sm"
          />
        </div>

        <div className="flex items-center gap-3">
          <label className="text-sm">Qty</label>
          <input
            type="number"
            min={1}
            value={qty}
            onChange={e => setQty(parseInt(e.target.value, 10) || 1)}
            className="w-20 border border-background-dark rounded px-3 py-2 text-sm"
          />
        </div>

        <button
          type="button"
          disabled={!canAdd}
          onClick={handleAdd}
          className="bg-primary text-white rounded px-5 py-3 text-sm font-medium disabled:opacity-50"
        >
          {!inStock ? 'Sold out' : requirementsMet ? `Add to cart — ${formatCents(effectivePriceCents * qty)}` : 'Choose your options'}
        </button>

        {error && <div className="text-sm text-red-600">{error}</div>}
      </div>
    </div>
  );
};

export default ProductDetails;
